import React, { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useLocation } from "wouter";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2, CheckCircle, AlertTriangle, ArrowLeft, Key } from 'lucide-react';
import { getApiUrl } from "../config";
import { extractSafeToken } from "@/utils/security";

const resetPasswordSchema = z.object({
  password: z.string()
    .min(12, "Password must be at least 12 characters")
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?].*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/, 
      "Password must contain at least 1 uppercase letter, 1 lowercase letter, 2 numbers, and 2 special characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export default function ResetPasswordPage() {
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isValidToken, setIsValidToken] = useState(true);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    // Extract and validate token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = extractSafeToken(urlParams, 'token');
    
    if (!tokenFromUrl) {
      setIsValidToken(false);
      toast({
        title: "Invalid reset link",
        description: "This password reset link is invalid, expired, or contains unsafe content.",
        variant: "destructive",
      });
    } else {
      setToken(tokenFromUrl);
    }
  }, [toast]);

  const onSubmit = async (data: ResetPasswordFormValues) => {
    if (!token) {
      toast({
        title: "Invalid reset link",
        description: "This password reset link is invalid or expired.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const res = await fetch(getApiUrl("/api/reset-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          password: data.password,
        }),
        credentials: "include"
      });
      
      const responseData = await res.json();
      
      if (!res.ok) {
        throw new Error(responseData.message || "Failed to reset password");
      }
      
      setIsSuccess(true);
      toast({
        title: "Password reset successful",
        description: "Your password has been updated. You can now log in with your new password.",
        className: "bg-white text-black border-zinc-200",
      });
    } catch (error: any) {
      toast({
        title: "Failed to reset password",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isValidToken) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row bg-gray-50 dark:bg-gray-900">
        {/* Left column – Error message */}
        <main className="flex flex-col items-center justify-center w-full lg:w-1/2 p-4 h-screen">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden dark:border dark:border-gray-700 flex flex-col items-center">
            <div className="p-8 w-full text-center">
              <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-6" />
              <h2 className="text-2xl font-semibold tracking-wide text-center text-gray-800 dark:text-white mb-4">
                Invalid Reset Link
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                This password reset link is invalid or has expired. 
                Please request a new password reset link.
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => window.location.href = '/forgot-password'}
                  variant="default"
                  className="w-full"
                >
                  Request New Reset Link
                </Button>
                <Button 
                  onClick={() => window.location.href = '/login'}
                  variant="outline"
                  className="w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </div>
            </div>
          </div>
        </main>

        {/* Right column – Splash image */}
        <div className="w-full lg:w-1/2 relative hidden lg:block">
          <img src="/assets/login_spash.png" alt="Reset password visual" className="w-full h-full object-cover" />
          <div className="w-full h-full bg-black/50 absolute top-0 left-0" />
          <div className="absolute bottom-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-3xl font-bold">
            <p className="font-bold">Connecting the blocks.</p>
            <p className="font-normal">Search. Discover. Analyze.</p>
          </div>
          <img src="/assets/Blockseer-logo-white.png" alt="Blockseer Logo" className="absolute top-4 right-4 w-56" />
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row bg-gray-50 dark:bg-gray-900">
        {/* Left column – Success message */}
        <main className="flex flex-col items-center justify-center w-full lg:w-1/2 p-4 h-screen">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden dark:border dark:border-gray-700 flex flex-col items-center">
            <div className="p-8 w-full text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
              <h2 className="text-2xl font-semibold tracking-wide text-center text-gray-800 dark:text-white mb-4">
                Password Reset Complete
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Your password has been successfully updated. 
                You can now log in with your new password.
              </p>
              <Button 
                onClick={() => window.location.href = '/login'}
                variant="default"
                className="w-full"
              >
                Continue to Login
              </Button>
            </div>
          </div>
        </main>

        {/* Right column – Splash image */}
        <div className="w-full lg:w-1/2 relative hidden lg:block">
          <img src="/assets/login_spash.png" alt="Reset password visual" className="w-full h-full object-cover" />
          <div className="w-full h-full bg-black/50 absolute top-0 left-0" />
          <div className="absolute bottom-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-3xl font-bold">
            <p className="font-bold">Connecting the blocks.</p>
            <p className="font-normal">Search. Discover. Analyze.</p>
          </div>
          <img src="/assets/Blockseer-logo-white.png" alt="Blockseer Logo" className="absolute top-4 right-4 w-56" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gray-50 dark:bg-gray-900">
      {/* Left column – Reset password form */}
      <main className="flex flex-col items-center justify-center w-full lg:w-1/2 p-4 h-screen">
        <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden dark:border dark:border-gray-700 flex flex-col items-center">
          <div className="p-8 w-full">
            <div className="text-center mb-8">
              <Key className="h-12 w-12 text-[#4A6CA7] mx-auto mb-4" />
              <h2 className="text-2xl font-semibold tracking-wide text-gray-800 dark:text-white mb-2">
                Reset Your Password
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                Enter your new password below. Make sure it's strong and secure.
              </p>
            </div>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-1">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Password
                  </label>
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="relative">
                            <Input 
                              type={showPassword ? "text" : "password"} 
                              placeholder="Enter your new password" 
                              className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10" 
                              {...field} 
                            />
                            <button 
                              type="button"
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              onClick={() => setShowPassword(!showPassword)}
                              tabIndex={-1}
                            >
                              {showPassword ? (
                                <EyeOff className="h-5 w-5 text-gray-400" />
                              ) : (
                                <Eye className="h-5 w-5 text-gray-400" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500 text-xs" />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-1">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Confirm New Password
                  </label>
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="relative">
                            <Input 
                              type={showConfirmPassword ? "text" : "password"} 
                              placeholder="Confirm your new password" 
                              className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10" 
                              {...field} 
                            />
                            <button 
                              type="button"
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              tabIndex={-1}
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="h-5 w-5 text-gray-400" />
                              ) : (
                                <Eye className="h-5 w-5 text-gray-400" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500 text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
                
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Password must contain at least 12 characters, including 1 uppercase letter, 
                  1 lowercase letter, 2 numbers, and 2 special characters.
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  variant="default"
                  className="w-full"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      <span>Resetting...</span>
                    </span>
                  ) : "Reset Password"}
                </Button>
              </form>
            </Form>
          </div>
        </div>
        
        <div className="mt-6 text-center">
          <span className="text-gray-600 dark:text-gray-300 text-sm">Remember your password? </span>
          <a href="/login" className="text-[#4A6CA7] hover:text-[#0A51A8] text-sm font-medium underline">
            Back to Login
          </a>
        </div>
        
        <footer className="py-4 text-center text-gray-500 text-sm lg:w-full">
          © blockseer.com. All rights reserved. Version 1.0.0
        </footer>
      </main>

      {/* Right column – Splash image */}
      <div className="w-full lg:w-1/2 relative hidden lg:block">
        <img src="/assets/login_spash.png" alt="Reset password visual" className="w-full h-full object-cover" />
        <div className="w-full h-full bg-black/50 absolute top-0 left-0" />
        <div className="absolute bottom-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-3xl font-bold">
          <p className="font-bold">Connecting the blocks.</p>
          <p className="font-normal">Search. Discover. Analyze.</p>
        </div>
        <img src="/assets/Blockseer-logo-white.png" alt="Blockseer Logo" className="absolute top-4 right-4 w-56" />
      </div>
    </div>
  );
}